// src/services/fingerprintParser.js
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

export class FingerprintParser {
  constructor() {
    this.dateColumns = {};
    this.employees = [];
  }

  /**
   * Detect CSV format type
   */
  detectCSVFormat(content) {
    // Normalize line endings
    const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedContent.trim().split('\n');
    
    // Check for calendar-style format
    const hasAttendanceLogs = lines.some(line => line.includes('Attendance Logs'));
    const hasPeriod = lines.some(line => line.includes('Period :'));
    const hasUserID = lines.some(line => line.includes('User ID') && line.includes('Name'));
    
    if (hasAttendanceLogs && hasPeriod && hasUserID) {
      return 'calendar';
    }
    
    // Check for traditional CSV format
    const firstLine = lines[0].toLowerCase();
    if (firstLine.includes('employee_id') || firstLine.includes('datetime') || firstLine.includes('fingerprint_id')) {
      return 'traditional';
    }
    
    return 'unknown';
  }

  /**
   * Parse calendar-style CSV from new fingerprint machine
   */
  parseCalendarCSV(content) {
    // Normalize line endings
    const normalizedContent = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalizedContent.split('\n').map(line => line.trim()).filter(line => line);
    
    console.log('Parsing calendar CSV with', lines.length, 'lines');
    
    // Extract period dates
    const periodLine = lines.find(line => line.includes('Period :'));
    const dateRange = this.extractDateRange(periodLine);
    
    if (!dateRange) {
      throw new Error('Could not extract date range from CSV');
    }
    
    console.log('Date range:', dateRange.startDate.format('YYYY-MM-DD'), 'to', dateRange.endDate.format('YYYY-MM-DD'));
    
    // Find header rows
    const userHeaderIndex = lines.findIndex(line => 
      line.includes('User ID') && line.includes('Name') && line.includes('Department')
    );
    
    if (userHeaderIndex === -1) {
      throw new Error('Could not find header row in calendar CSV');
    }

    // Parse headers to get date columns
    const dayHeaders = this.parseCSVLine(lines[userHeaderIndex]);
    const dayOfWeekHeaders = userHeaderIndex + 1 < lines.length ? 
      this.parseCSVLine(lines[userHeaderIndex + 1]) : [];
    
    console.log('Day headers:', dayHeaders.slice(3, 10)); // Show first few date columns
    
    this.dateColumns = this.mapColumnsToDates(dayHeaders, dayOfWeekHeaders, dateRange);
    
    // Parse employee data
    const employees = this.parseEmployeeData(lines, userHeaderIndex + 2);
    
    console.log('Parsed', employees.length, 'employees');
    
    // Convert to standard format
    const standardEntries = this.convertToStandardFormat(employees, dateRange);
    
    console.log('Converted to', standardEntries.length, 'standard entries');
    
    return {
      format: 'calendar',
      period: dateRange,
      employees: employees,
      entries: standardEntries,
      summary: {
        totalEmployees: employees.length,
        totalEntries: standardEntries.length,
        dateRange: {
          start: dateRange.startDate.format('YYYY-MM-DD'),
          end: dateRange.endDate.format('YYYY-MM-DD')
        }
      }
    };
  }

  /**
   * Extract date range from period line
   */
  extractDateRange(periodLine) {
    if (!periodLine) return null;
    
    console.log('Extracting date range from:', periodLine);
    
    // Match formats like "5/28/2025 ~ 6/13/2025" or "28/05/2025 ~ 13/06/2025"
    const match = periodLine.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s*~\s*(\d{1,2}\/\d{1,2}\/\d{4})/);
    if (match) {
      // Try M/D/YYYY format first
      let startDate = dayjs(match[1], 'M/D/YYYY');
      let endDate = dayjs(match[2], 'M/D/YYYY');
      
      // If dates seem invalid (e.g., future dates), try D/M/YYYY format
      if (!startDate.isValid() || startDate.year() > dayjs().year() + 1) {
        startDate = dayjs(match[1], 'D/M/YYYY');
        endDate = dayjs(match[2], 'D/M/YYYY');
      }
      
      // For the specific case in the CSV (future dates), let's correct the year
      if (startDate.year() === 2025 && dayjs().year() < 2025) {
        // Assume it's actually 2024 data
        startDate = startDate.year(2024);
        endDate = endDate.year(2024);
      }
      
      console.log('Parsed dates:', startDate.format('YYYY-MM-DD'), 'to', endDate.format('YYYY-MM-DD'));
      
      return {
        startDate: startDate,
        endDate: endDate
      };
    }
    return null;
  }

  /**
   * Map CSV columns to actual dates
   */
  mapColumnsToDates(dayHeaders, dayOfWeekHeaders, dateRange) {
    const dateColumns = {};
    
    if (!dateRange) return dateColumns;
    
    // Start from column 3 (after User ID, Name, Department)
    let columnIndex = 3;
    
    // The date numbers in the header correspond to days of the period
    const startDate = dateRange.startDate;
    const endDate = dateRange.endDate;
    
    // FIXED: Track which dates have already been assigned to columns
    // This prevents duplicate day numbers (e.g., 28, 29) from being mapped to the same date
    const usedDates = new Set();
    
    // First, let's map the date numbers from the header
    for (let i = columnIndex; i < dayHeaders.length; i++) {
      const dayNumber = dayHeaders[i];
      
      if (dayNumber && !isNaN(parseInt(dayNumber))) {
        const dayNum = parseInt(dayNumber);
        
        // Find the corresponding date (skip already used dates)
        let currentDate = startDate.clone();
        while (currentDate.isSameOrBefore(endDate)) {
          const dateKey = currentDate.format('YYYY-MM-DD');
          
          // Only assign this date if it matches the day number AND hasn't been used yet
          if (currentDate.date() === dayNum && !usedDates.has(dateKey)) {
            dateColumns[i] = {
              date: currentDate.clone(),
              dayNumber: dayNum,
              dayOfWeek: dayOfWeekHeaders[i] || ''
            };
            usedDates.add(dateKey);  // Mark this date as used
            break;
          }
          currentDate = currentDate.add(1, 'day');
        }
      }
    }
    
    console.log('Mapped', Object.keys(dateColumns).length, 'date columns');
    
    return dateColumns;
  }

  /**
   * Parse employee attendance data
   */
  parseEmployeeData(lines, startIndex) {
    const employees = [];
    let currentEmployee = null;
    let i = startIndex;
    
    while (i < lines.length) {
      const line = lines[i];
      if (!line || line.trim() === '') {
        i++;
        continue;
      }
      
      const cells = this.parseCSVLine(line);
      const employeeId = cells[0] && cells[0].trim();
      const employeeName = cells[1] && cells[1].trim();
      const department = cells[2] && cells[2].trim();
      
      // Check if this is a new employee record (has employee ID)
      if (employeeId && !isNaN(parseInt(employeeId))) {
        // Save previous employee if exists
        if (currentEmployee && currentEmployee.timeEntries.length > 0) {
          employees.push(currentEmployee);
        }
        
        currentEmployee = {
          fingerprint_id: employeeId,
          name: employeeName || `Employee_${employeeId}`,
          department: department || 'UNKNOWN',
          timeEntries: []
        };
        
        // Parse time entries from this line
        this.parseTimeEntriesFromLine(cells, currentEmployee);
      } else if (currentEmployee) {
        // This is a continuation line for the current employee (additional time entries)
        this.parseTimeEntriesFromLine(cells, currentEmployee);
      }
      
      i++;
    }
    
    // Add the last employee
    if (currentEmployee && currentEmployee.timeEntries.length > 0) {
      employees.push(currentEmployee);
    }
    
    return employees;
  }

  /**
   * Parse time entries from a line
   */
  parseTimeEntriesFromLine(cells, employee) {
    for (const [columnIndex, dateInfo] of Object.entries(this.dateColumns)) {
      const cellIndex = parseInt(columnIndex);
      const timeEntry = cells[cellIndex];
      
      if (timeEntry && timeEntry.trim() && timeEntry.trim() !== '') {
        const parsedTime = this.parseTimeEntry(timeEntry.trim());
        if (parsedTime) {
          employee.timeEntries.push({
            date: dateInfo.date,
            time: parsedTime,
            originalEntry: timeEntry.trim()
          });
        }
      }
    }
  }

  /**
   * Parse CSV line handling quoted fields
   */
  parseCSVLine(line) {
    const cells = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      
      if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        cells.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    
    cells.push(current.trim());
    return cells;
  }

  /**
   * Parse time entry (e.g., "8:45 AM", "17:30", "5:46 PM")
   */
  parseTimeEntry(timeStr) {
    if (!timeStr || timeStr === '') return null;
    
    // Handle AM/PM format
    const ampmMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
    if (ampmMatch) {
      let hours = parseInt(ampmMatch[1]);
      const minutes = parseInt(ampmMatch[2]);
      const period = ampmMatch[3].toUpperCase();
      
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      return {
        hours,
        minutes,
        timeString: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
      };
    }
    
    // Handle 24-hour format
    const hourMatch = timeStr.match(/(\d{1,2}):(\d{2})/);
    if (hourMatch) {
      const hours = parseInt(hourMatch[1]);
      const minutes = parseInt(hourMatch[2]);
      
      return {
        hours,
        minutes,
        timeString: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:00`
      };
    }
    
    return null;
  }

  /**
   * Convert to standard timesheet format
   */
  convertToStandardFormat(employees, dateRange) {
    const entries = [];
    
    employees.forEach(employee => {
      // Group time entries by date
      const entriesByDate = {};
      
      employee.timeEntries.forEach(entry => {
        const dateKey = entry.date.format('YYYY-MM-DD');
        if (!entriesByDate[dateKey]) {
          entriesByDate[dateKey] = [];
        }
        entriesByDate[dateKey].push(entry);
      });
      
      // Process each date's entries
      Object.entries(entriesByDate).forEach(([date, dateEntries]) => {
        // Sort entries by time
        dateEntries.sort((a, b) => {
          const timeA = a.time.hours * 60 + a.time.minutes;
          const timeB = b.time.hours * 60 + b.time.minutes;
          return timeA - timeB;
        });
        
        // Create entries for each time
        dateEntries.forEach((entry, index) => {
          const datetime = entry.date.clone()
            .hour(entry.time.hours)
            .minute(entry.time.minutes)
            .second(0);
          
          // Determine action based on position and time
          // First entry or morning entries are typically check-in
          // Last entry or evening entries are typically check-out
          let action = '01'; // Default to check-in
          
          if (dateEntries.length === 1) {
            // Single entry - determine by time
            action = entry.time.hours >= 12 ? '02' : '01';
          } else if (index === dateEntries.length - 1) {
            // Last entry of the day
            action = '02';
          } else if (index === 0) {
            // First entry of the day
            action = '01';
          } else {
            // Middle entries - alternate or check time
            action = entry.time.hours >= 12 ? '02' : '01';
          }
          
          entries.push({
            employee_id: employee.fingerprint_id,
            datetime: datetime.format('YYYY-MM-DD HH:mm:ss'),
            status: 'present',
            action: action,
            device_id: 'FP001',  // Changed from 'FINGERPRINT' to fit varchar(10)
            name: employee.name
          });
        });
      });
    });
    
    // Sort all entries by datetime
    entries.sort((a, b) => dayjs(a.datetime).unix() - dayjs(b.datetime).unix());
    
    return entries;
  }

  /**
   * Validate parsed data
   */
  validateParsedData(parsedData) {
    const errors = [];
    const warnings = [];
    
    if (!parsedData.employees || parsedData.employees.length === 0) {
      errors.push('No employees found in the CSV');
    }
    
    if (!parsedData.entries || parsedData.entries.length === 0) {
      errors.push('No time entries found in the CSV');
    }
    
    // Check for employees without time entries
    const employeesWithoutEntries = parsedData.employees.filter(emp => 
      emp.timeEntries.length === 0
    );
    
    if (employeesWithoutEntries.length > 0) {
      warnings.push(`${employeesWithoutEntries.length} employees have no time entries`);
    }
    
    // Check for potential data issues
    parsedData.employees.forEach(employee => {
      if (!employee.name || employee.name.startsWith('Employee_')) {
        warnings.push(`Employee ${employee.fingerprint_id} has no name`);
      }
      
      if (employee.department === 'UNKNOWN') {
        warnings.push(`Employee ${employee.fingerprint_id} has no department`);
      }
    });
    
    return {
      isValid: errors.length === 0,
      errors,
      warnings,
      stats: {
        totalEmployees: parsedData.employees.length,
        totalEntries: parsedData.entries.length,
        employeesWithoutEntries: employeesWithoutEntries.length
      }
    };
  }
}

export default FingerprintParser;